"use client";

import * as React from "react";
import { ComponentProps, useRef, useEffect, useState } from "react";
import { Collapsible as CollapsiblePrimitive } from "radix-ui";
import { ChevronIcon } from "./icons/functional/ChevronIcon";
import { CrossIcon } from "./icons/functional/CrossIcon";
import { cn } from "@repo/utils/cn";
import { text, Text } from "./Text";

function useOnClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: (event: MouseEvent | TouchEvent) => void,
) {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

export type SelectOption = {
  label: string;
  value: string;
  displayLabel?: string;
};

export type SelectProps = {
  label: string;
  options: SelectOption[];
  value?: string | string[];
  onValueChange?: (value: string[] | string) => void;
  multiselect?: boolean;
  renderType?: "default" | "checkbox";
  mode?: "default" | "bright";
  state?: "default" | "error" | "disabled";
  errorMessage?: string;
  maxHeight?: number;
  optionsGap?: string;
  renderOption?: (option: SelectOption, isSelected: boolean) => React.ReactNode;
} & ComponentProps<typeof CollapsiblePrimitive.Root>;

function useSingleSelect(options: SelectOption[], value?: string | string[]) {
  const selectedValue = Array.isArray(value) ? undefined : value;
  const selectedOption = options.find((opt) => opt.value === selectedValue);

  return {
    selectedValue,
    selectedOption,
    selectedLabel: selectedOption?.displayLabel || selectedOption?.label,
    isOptionSelected: (optionValue: string) => selectedValue === optionValue,
  };
}

function useMultiSelect(options: SelectOption[], value?: string | string[]) {
  const selectedValues = Array.isArray(value) ? value : [];

  const getSelectedLabel = () => {
    if (selectedValues.length === 0) {
      return undefined;
    } else if (selectedValues.length === 1) {
      const selected = options.find((opt) => opt.value === selectedValues[0]);
      return selected?.displayLabel || selected?.label;
    } else {
      return `${selectedValues.length} selected`;
    }
  };

  return {
    selectedValues,
    selectedLabel: getSelectedLabel(),
    isOptionSelected: (optionValue: string) =>
      selectedValues.includes(optionValue),
    handleOptionToggle: (
      optionValue: string,
      onValueChange?: (value: string[]) => void,
    ) => {
      if (selectedValues.includes(optionValue)) {
        onValueChange?.(selectedValues.filter((val) => val !== optionValue));
      } else {
        onValueChange?.([...selectedValues, optionValue]);
      }
    },
  };
}

export function Select({
  label,
  options,
  value,
  onValueChange,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  multiselect = false,
  renderType = "default",
  mode = "default",
  state = "default",
  errorMessage,
  maxHeight,
  optionsGap,
  renderOption,
  ...props
}: SelectProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = (newOpenState: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpenState);
    }
    controlledOnOpenChange?.(newOpenState);
  };

  const singleSelect = useSingleSelect(options, value);
  const multiSelect = useMultiSelect(options, value);

  const { selectedLabel, isOptionSelected } = multiselect
    ? multiSelect
    : singleSelect;

  useOnClickOutside(containerRef, () => {
    if (open) {
      handleOpenChange(false);
    }
  });

  const renderDefaultOption = (option: SelectOption, isSelected: boolean) => (
    <>
      {renderCheckbox(multiselect, renderType, isSelected)}
      <span className="ui:truncate">{option.label}</span>
    </>
  );

  const handleOptionClick = (optionValue: string) => {
    if (multiselect) {
      multiSelect.handleOptionToggle(optionValue, (newValue) =>
        onValueChange?.(newValue as string[]),
      );
    } else {
      onValueChange?.(optionValue);
      handleOpenChange(false);
    }
  };

  return (
    <div ref={containerRef} className={"ui:relative ui:w-full"}>
      <CollapsiblePrimitive.Root
        open={open}
        onOpenChange={handleOpenChange}
        className={"ui:flex ui:w-full ui:flex-col"}
        {...props}
      >
        <SelectTrigger
          isOpen={open}
          options={options}
          label={label}
          value={value}
          selectedLabel={selectedLabel}
          mode={mode}
          state={state}
        />
        <CollapsiblePrimitive.Content
          className={cn(
            "ui:absolute ui:top-full ui:z-50 ui:w-full ui:flex-col ui:gap-2 ui:bg-neutral-white ui:p-sectionContained ui:pl-tiny ui:shadow-lg",
            "ui:data-[state=open]:animate-slideDownAndFadeIn",
          )}
          style={{ maxHeight }}
        >
          <div
            className={cn(
              "ui:-mx-small ui:flex ui:flex-col ui:overflow-y-auto ui:px-medium",
              optionsGap,
              "ui:scrollbar-thin ui:scrollbar-track-transparent ui:scrollbar-thumb-neutral-300 hover:ui:scrollbar-thumb-neutral-400",
            )}
            style={{ maxHeight: maxHeight ? maxHeight - 40 : undefined }}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "ui:flex ui:min-h-8 ui:w-full ui:cursor-pointer ui:items-center ui:text-left ui:text-navButton ui:text-textSecondary ui:transition-colors ui:outline-none",
                  "ui:hover:text-actionPrimaryHover ui:focus-visible:text-actionPrimaryHover",
                  {
                    "ui:text-actionPrimaryHover": isOptionSelected(
                      option.value,
                    ),
                  },
                )}
                onClick={() => handleOptionClick(option.value)}
              >
                {renderOption
                  ? renderOption(option, isOptionSelected(option.value))
                  : renderDefaultOption(option, isOptionSelected(option.value))}
              </button>
            ))}
          </div>
        </CollapsiblePrimitive.Content>
      </CollapsiblePrimitive.Root>

      {state === "error" && errorMessage && (
        <span className={"ui:mt-2 ui:flex ui:items-center ui:gap-tiny"}>
          <span
            className={
              "ui:flex ui:h-6 ui:w-6 ui:shrink-0 ui:items-center ui:justify-center ui:rounded-full ui:bg-functionalNegative ui:text-neutral-white"
            }
          >
            <CrossIcon className={"ui:h-3.5 ui:w-3.5"} />
          </span>
          <Text
            variant={"micro"}
            className={cn(
              mode === "bright"
                ? "ui:text-textAmplified"
                : "ui:text-textSecondary",
            )}
          >
            {errorMessage}
          </Text>
        </span>
      )}
    </div>
  );
}

function renderCheckbox(
  multiselect: boolean,
  renderType: string,
  isSelected: boolean,
) {
  if (!multiselect || renderType !== "checkbox") return null;

  return (
    <div className="ui:mr-2 ui:flex ui:h-4 ui:w-4 ui:items-center ui:justify-center ui:rounded ui:border ui:border-neutral-400">
      {isSelected && (
        <svg
          className="ui:h-3 ui:w-3 ui:text-actionPrimaryHover"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </div>
  );
}

const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Trigger> & {
    label?: string;
    isOpen?: boolean;
    options: SelectOption[];
    value?: string | string[];
    selectedLabel?: string;
    mode?: "default" | "bright";
    state?: "default" | "error" | "disabled";
  }
>(
  (
    {
      className,
      label,
      isOpen,
      value,
      selectedLabel,
      mode = "default",
      state = "default",
      ...props
    },
    ref,
  ) => (
    <CollapsiblePrimitive.Trigger
      ref={ref}
      disabled={state === "disabled"}
      className={cn(
        "ui:flex ui:h-16 ui:w-full ui:cursor-pointer ui:items-end ui:border-b-2 ui:pt-1 ui:pb-gapExtraSmall ui:text-left ui:outline-none",
        {
          "ui:border-borderMuted ui:focus:border-borderAmplified":
            state !== "error" && mode === "default",
          "ui:border-brand-300 ui:focus:border-borderAmplified":
            state !== "error" && mode === "bright",
          "ui:border-functionalNegative": state === "error",
        },
        "ui:disabled:cursor-default",
        mode === "bright"
          ? "ui:disabled:border-brand-300 ui:disabled:text-brand-300"
          : "ui:disabled:border-actionIconOutlineDisabled ui:disabled:text-actionIconOutlineDisabled",
        className,
      )}
      {...props}
    >
      <div className={"ui:relative ui:-mb-0.5 ui:flex ui:w-full ui:items-end"}>
        <span
          className={cn(
            "ui:absolute ui:left-0 ui:mt-auto ui:select-none",
            "ui:transition-all",
            "ui:text-body ui:font-extrabold ui:tracking-[-0.16px]",
            mode === "bright"
              ? "ui:text-textAmplified"
              : "ui:text-textSecondary",
            "ui:group-focus-within:-translate-y-6 ui:group-focus-within:text-micro ui:group-focus-within:font-medium ui:group-focus-within:tracking-[-0.14px]",
            {
              "ui:-translate-y-6 ui:text-micro ui:font-medium ui:tracking-[-0.14px]":
                !!value && (Array.isArray(value) ? value.length > 0 : true),
              "ui:text-actionIconOutlineDisabled":
                state === "disabled" && mode === "default",
              "ui:text-brand-300": state === "disabled" && mode === "bright",
            },
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            "ui:mt-auto",
            text({ variant: "body", weight: "strong" }),
            "ui:text-textAmplified",
          )}
        >
          {selectedLabel}
        </span>
        <ChevronIcon
          className={cn(
            "ui:absolute ui:right-0 ui:h-6 ui:w-6 ui:text-textAmplified ui:transition-transform",
            {
              "ui:rotate-180": !isOpen,
            },
          )}
        />
      </div>
    </CollapsiblePrimitive.Trigger>
  ),
);
SelectTrigger.displayName = "SelectTrigger";
